import React, { useState, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Sparkles, Camera, Upload, ArrowLeft, ArrowRight,
  Loader2, CheckCircle2, Copy, Mail, Bot, AlertTriangle,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { DogPicker } from '../components/DogPicker';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY_VALUES = [
  '', 'none', 'n/a', 'na', 'unknown', 'not visible', 'not specified',
  'not available', 'not provided', 'not listed', 'not shown', 'unspecified',
  'no info', 'no information', 'undefined', 'null',
];

const isFieldFilled = (v) => {
  if (v == null) return false;
  if (typeof v === 'string') {
    const low = v.trim().toLowerCase();
    return !!low && !EMPTY_VALUES.includes(low);
  }
  return true;
};

const readFileAsBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result.split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const STEPS = ['upload', 'review', 'details', 'success'];

const CRITICAL_FIELDS = [
  { key: 'brand', label: 'Full brand name' },
  { key: 'product_name', label: 'Full product name' },
  { key: 'ingredients', label: 'Composition / ingredients' },
  { key: 'analytical_constituents', label: 'Analytical constituents (% values)' },
  { key: 'nutritional_additives', label: 'Nutritional additives panel' },
];

const TikTokLoader = ({ label }) => (
  <motion.div
    key={label}
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    className="text-center py-8"
  >
    <div className="inline-flex items-center gap-2 text-[#1f6d62] font-semibold text-sm">
      <Loader2 className="w-4 h-4 animate-spin" />
      {label}
    </div>
  </motion.div>
);

export const TikTokLive = () => {
  const { customer } = useAuth();
  const [step, setStep] = useState('upload');
  const [frontFile, setFrontFile] = useState(null);
  const [backFile, setBackFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [imageUrls, setImageUrls] = useState({ front: null, back: null });
  const [email, setEmail] = useState(customer?.email || '');
  const [dogName, setDogName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reference, setReference] = useState(null);
  const [nextDate, setNextDate] = useState(null);
  const [uploadingExtra, setUploadingExtra] = useState(false);

  const frontInputRef = useRef(null);
  const backInputRef = useRef(null);
  const extraInputRef = useRef(null);

  const missingFields = analysis
    ? CRITICAL_FIELDS.filter(f => !isFieldFilled(analysis[f.key]))
    : CRITICAL_FIELDS;
  const filledCount = CRITICAL_FIELDS.length - missingFields.length;
  const completePct = Math.round((filledCount / CRITICAL_FIELDS.length) * 100);
  const isComplete = missingFields.length === 0;

  const reset = () => {
    setStep('upload');
    setFrontFile(null);
    setBackFile(null);
    setAnalysis(null);
    setImageUrls({ front: null, back: null });
    setDogName('');
    setReference(null);
    setNextDate(null);
  };

  const handleExtraImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      e.target.value = '';
      return;
    }
    setUploadingExtra(true);
    try {
      const b64 = await readFileAsBase64(file);
      const hint = missingFields.slice(0, 3).map(f => f.label).join(', ');
      const res = await axios.post(`${API}/ted-ai/analyze-extra`, {
        current_analysis: analysis,
        image_base64: b64,
        hint: hint || null,
      });
      if (res.data.status === 'success') {
        setAnalysis(res.data.analysis);
        const filled = res.data.filled_fields || [];
        if (filled.length) {
          toast.success(`Ted read: ${filled.join(', ')}`);
        } else {
          toast('No new details found — try a clearer close-up.');
        }
      } else {
        toast.error(res.data.message || 'Could not read that image');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Extra scan failed');
    } finally {
      setUploadingExtra(false);
      e.target.value = '';
    }
  };

  const handleFile = (which) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image must be under 10MB');
      e.target.value = '';
      return;
    }
    if (which === 'front') setFrontFile(file);
    else setBackFile(file);
  };

  const handleAnalyze = async () => {
    if (!frontFile || !backFile) {
      toast.error('Please upload both front and back photos');
      return;
    }
    setAnalyzing(true);
    try {
      const [front_image, back_image] = await Promise.all([
        readFileAsBase64(frontFile),
        readFileAsBase64(backFile),
      ]);
      const res = await axios.post(`${API}/ted-ai/analyze`, { front_image, back_image });
      if (res.data.status !== 'success') {
        toast.error(res.data.message || 'Could not read the bag. Try clearer photos.');
        return;
      }
      setAnalysis(res.data.analysis);
      setImageUrls({ front: res.data.front_image_url, back: res.data.back_image_url });
      setStep('review');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Scan failed — please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim() || !dogName.trim()) {
      toast.error('Please enter your email and dog name');
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/tiktok-live/submit`, {
        email: email.trim(),
        dog_name: dogName.trim(),
        analysis,
        front_image_url: imageUrls.front,
        back_image_url: imageUrls.back,
      });
      setReference(res.data.reference);
      setNextDate(res.data.next_date);
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const stepIndex = STEPS.indexOf(step);

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-0">
      {/* Hero */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-none sm:rounded-3xl mt-0 sm:mt-6 mb-6 -mx-4 sm:mx-0 px-6 sm:px-10 py-6 sm:py-8"
        style={{
          backgroundColor: '#1f6d62',
          backgroundImage: `
            radial-gradient(circle at 20% 15%, rgba(160, 218, 57, 0.12) 0%, transparent 45%),
            radial-gradient(circle at 85% 80%, rgba(160, 218, 57, 0.10) 0%, transparent 50%),
            url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' seed='7'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.08 0'/></filter><rect width='100%25' height='100%25' filter='url(%23n)'/></svg>")
          `,
        }}
        data-testid="tiktok-live-hero"
      >
        <div className="relative flex items-center gap-4 sm:gap-6">
          <div className="flex-1 min-w-0">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-widest text-[#1f6d62] bg-[#a0da39] mb-2">
              <Sparkles className="w-3 h-3" /> TikTok Live
            </span>
            <h2 className="text-white text-2xl sm:text-3xl md:text-4xl font-extrabold uppercase tracking-tight leading-[0.95]">
              Submit Your <span className="text-[#a0da39]">Bag</span>
            </h2>
            <p
              className="text-[#a0da39] mt-1 text-lg sm:text-xl italic leading-tight"
              style={{ fontFamily: "'Caveat', 'Dancing Script', cursive" }}
            >
              See your match live on TikTok.
            </p>
            <p className="text-white/85 mt-2 text-xs sm:text-sm leading-relaxed">
              Scan your bag, give us your details, and we'll recommend live on TikTok.
            </p>
          </div>
          <div className="relative flex-shrink-0 w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-white/15 border-4 border-[#a0da39] shadow-lg flex items-center justify-center">
            <Bot className="w-10 h-10 text-[#a0da39]" />
          </div>
        </div>
      </motion.div>

      {/* Progress */}
      {step !== 'success' && (
        <div className="flex items-center justify-center gap-2 mb-6" data-testid="ttl-progress">
          {STEPS.slice(0, 3).map((s, i) => (
            <React.Fragment key={s}>
              <div
                className={`h-2 rounded-full transition-all ${
                  i <= stepIndex ? 'w-10 bg-[#1f6d62]' : 'w-2 bg-gray-200'
                }`}
              />
              {i < 2 && <div className="w-1 h-1 rounded-full bg-gray-200" />}
            </React.Fragment>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        {step === 'upload' && (
          <motion.div
            key="upload"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="text-center mb-4">
              <h3 className="font-bold text-[#222] text-lg">Upload Your Bag Photos</h3>
              <p className="text-sm text-[#666]">Snap a clear front and back photo</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {[
                { which: 'front', file: frontFile, ref: frontInputRef, label: 'Front of Bag' },
                { which: 'back', file: backFile, ref: backInputRef, label: 'Back of Bag' },
              ].map(({ which, file, ref, label }) => (
                <button
                  key={which}
                  type="button"
                  onClick={() => ref.current?.click()}
                  className={`relative rounded-2xl border-2 border-dashed transition-colors overflow-hidden ${
                    file ? 'border-[#0FA49C] bg-[#0FA49C]/5' : 'border-gray-300 hover:border-[#0FA49C]/50 bg-gray-50'
                  }`}
                  data-testid={`ttl-${which}-upload`}
                >
                  {file ? (
                    <img src={URL.createObjectURL(file)} alt={label} className="w-full aspect-square object-cover" />
                  ) : (
                    <div className="aspect-square flex flex-col items-center justify-center p-6">
                      <div className="w-12 h-12 rounded-2xl bg-[#0FA49C]/10 flex items-center justify-center mb-3">
                        <Camera className="w-6 h-6 text-[#0FA49C]" />
                      </div>
                      <p className="font-semibold text-[#222] text-sm">{label}</p>
                      <p className="text-xs text-[#999] mt-1">Tap to take a photo</p>
                    </div>
                  )}
                  <input
                    ref={ref}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={handleFile(which)}
                  />
                  {file && (
                    <div className="absolute top-2 right-2 bg-[#0FA49C] text-white rounded-full w-6 h-6 flex items-center justify-center">
                      <CheckCircle2 className="w-4 h-4" />
                    </div>
                  )}
                </button>
              ))}
            </div>

            {analyzing ? (
              <TikTokLoader label="Ted is reading your bag…" />
            ) : (
              <Button
                onClick={handleAnalyze}
                disabled={!frontFile || !backFile}
                className="w-full rounded-xl bg-[#1f6d62] hover:bg-[#174f46] text-white py-6 font-bold uppercase tracking-wide text-sm"
                data-testid="ttl-analyze-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                Continue
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            )}
          </motion.div>
        )}

        {step === 'review' && (
          <motion.div
            key="review"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-4"
          >
            <div className="text-center mb-2">
              <h3 className="font-bold text-[#222] text-lg">What Ted Could Read</h3>
              <p className="text-sm text-[#666]">We need all 5 items below for a full live match.</p>
            </div>

            {/* Completeness ring card */}
            <div
              className={`rounded-2xl border-2 p-4 ${
                isComplete
                  ? 'bg-green-50 border-green-300'
                  : missingFields.length <= 2
                    ? 'bg-amber-50 border-amber-300'
                    : 'bg-red-50 border-red-300'
              }`}
              data-testid="ttl-review-card"
            >
              <div className="flex items-center gap-4">
                <div className="relative w-16 h-16 flex-shrink-0">
                  <svg className="w-16 h-16 transform -rotate-90" viewBox="0 0 64 64">
                    <circle cx="32" cy="32" r="28" strokeWidth="6" className="stroke-white/60 fill-none" />
                    <circle
                      cx="32" cy="32" r="28" strokeWidth="6" strokeLinecap="round"
                      className={`fill-none ${
                        isComplete ? 'stroke-green-500'
                        : missingFields.length <= 2 ? 'stroke-amber-500'
                        : 'stroke-red-500'
                      }`}
                      strokeDasharray={2 * Math.PI * 28}
                      strokeDashoffset={2 * Math.PI * 28 * (1 - completePct / 100)}
                      style={{ transition: 'stroke-dashoffset 0.6s ease' }}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center">
                    <span className="text-sm font-extrabold text-[#222]" data-testid="ttl-complete-pct">
                      {filledCount}/{CRITICAL_FIELDS.length}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-[#222] text-sm">
                    {isComplete
                      ? "Ted read everything — you're all set"
                      : missingFields.length === 1
                        ? "Almost there — one more detail needed"
                        : `${missingFields.length} details still missing`}
                  </p>
                  <p className="text-xs text-[#666] mt-0.5">
                    {isComplete
                      ? 'Full match eligible. Tap Continue to submit.'
                      : 'Submit now for a partial match, or add more photos below.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Checklist */}
            <div className="rounded-2xl border border-gray-200 bg-white p-4 space-y-2" data-testid="ttl-checklist">
              {CRITICAL_FIELDS.map(f => {
                const filled = isFieldFilled(analysis?.[f.key]);
                const val = analysis?.[f.key];
                return (
                  <div key={f.key} className="flex items-start gap-3 text-sm">
                    <div className={`w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5 ${
                      filled ? 'bg-green-500 text-white' : 'bg-gray-100 border border-gray-300'
                    }`}>
                      {filled && <CheckCircle2 className="w-3.5 h-3.5" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`font-semibold ${filled ? 'text-[#222]' : 'text-[#999]'}`}>{f.label}</p>
                      {filled && typeof val === 'string' && (
                        <p className="text-xs text-[#666] mt-0.5 line-clamp-2 break-words">{val}</p>
                      )}
                      {!filled && (
                        <p className="text-xs text-red-600 mt-0.5">Not found on the images you uploaded</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Extra image uploader */}
            {!isComplete && (
              <div className="rounded-2xl bg-[#1f6d62]/5 border-2 border-dashed border-[#1f6d62]/30 p-4">
                <p className="text-sm font-semibold text-[#1f6d62] mb-1 flex items-center gap-1.5">
                  <Camera className="w-4 h-4" /> Add a close-up photo
                </p>
                <p className="text-xs text-[#444] mb-3 leading-relaxed">
                  {missingFields.some(f => f.key === 'analytical_constituents' || f.key === 'nutritional_additives') ? (
                    <>Focus on the <strong>nutrition panel</strong> (protein %, fat %, fibre %, ash %) and the <strong>additives list</strong>.</>
                  ) : missingFields.some(f => f.key === 'ingredients') ? (
                    <>Focus on the <strong>composition / ingredients list</strong> on the back of the bag.</>
                  ) : (
                    <>Snap the <strong>{missingFields[0]?.label}</strong> clearly.</>
                  )}
                </p>
                <input
                  ref={extraInputRef}
                  type="file"
                  accept="image/*"
                  capture="environment"
                  className="hidden"
                  onChange={handleExtraImage}
                  data-testid="ttl-extra-image-input"
                />
                <Button
                  type="button"
                  onClick={() => extraInputRef.current?.click()}
                  disabled={uploadingExtra}
                  className="w-full rounded-xl bg-[#1f6d62] hover:bg-[#174f46] text-white py-3 font-semibold"
                  data-testid="ttl-extra-image-btn"
                >
                  {uploadingExtra ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Reading image…</>
                  ) : (
                    <><Camera className="w-4 h-4 mr-2" /> Add close-up photo</>
                  )}
                </Button>
              </div>
            )}

            {/* Partial-match warning */}
            {!isComplete && (
              <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 flex items-start gap-2" data-testid="ttl-partial-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-amber-900 leading-relaxed">
                  <strong>Heads up:</strong> if you continue without the missing items, this will be submitted as a <strong>partial match</strong> — Ted will do his best on the live but a full bag read-out will be more accurate.
                </p>
              </div>
            )}

            <Button
              onClick={() => setStep('details')}
              className={`w-full rounded-xl py-6 font-bold uppercase tracking-wide text-sm ${
                isComplete ? 'bg-[#a0da39] hover:bg-[#8ac82f] text-[#1f3d2f]' : 'bg-[#1f6d62] hover:bg-[#174f46] text-white'
              }`}
              data-testid="ttl-continue-btn"
            >
              {isComplete ? 'Continue — Full Match' : 'Continue with Partial Match'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>

            <button
              type="button"
              onClick={() => setStep('upload')}
              className="w-full text-center text-sm text-[#666] hover:text-[#0FA49C] py-2"
              data-testid="ttl-review-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Re-take bag photos
            </button>
          </motion.div>
        )}

        {step === 'details' && (
          <motion.form
            key="details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onSubmit={handleSubmit}
            className="space-y-4"
            data-testid="ttl-details-form"
          >
            <div className="text-center mb-2">
              <h3 className="font-bold text-[#222] text-lg">Almost Done</h3>
              <p className="text-sm text-[#666]">Give us your details and we'll send your reference.</p>
            </div>

            {analysis && (
              <div className="rounded-2xl border border-gray-200 bg-white p-4">
                <p className="text-xs text-[#999] uppercase tracking-wide font-semibold mb-1">We read</p>
                <p className="font-semibold text-[#222] text-sm">
                  {isFieldFilled(analysis.brand) ? analysis.brand : '—'}{' · '}
                  {isFieldFilled(analysis.product_name) ? analysis.product_name : '—'}
                </p>
                {isFieldFilled(analysis.bag_weight) && (
                  <p className="text-xs text-[#666] mt-0.5">Bag weight: {analysis.bag_weight}</p>
                )}
              </div>
            )}

            <div>
              <label className="block text-sm font-semibold text-[#333] mb-1.5">Email *</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="pl-10"
                  data-testid="ttl-email-input"
                />
              </div>
            </div>

            <DogPicker
              value={dogName}
              onChange={setDogName}
              label="Dog's Name"
              required
              placeholder="e.g. Buddy"
              testId="ttl-dog-name"
            />

            <Button
              type="submit"
              disabled={submitting}
              className="w-full rounded-xl bg-[#1f6d62] hover:bg-[#174f46] text-white py-6 font-bold uppercase tracking-wide text-sm"
              data-testid="ttl-submit-btn"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>
              ) : (
                <>Get My Reference <ArrowRight className="w-4 h-4 ml-2" /></>
              )}
            </Button>

            <button
              type="button"
              onClick={() => setStep('upload')}
              className="w-full text-center text-sm text-[#666] hover:text-[#0FA49C] py-2"
              data-testid="ttl-back-btn"
            >
              <ArrowLeft className="w-3.5 h-3.5 inline mr-1" /> Re-take photos
            </button>
          </motion.form>
        )}

        {step === 'success' && (
          <motion.div
            key="success"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
            data-testid="ttl-success"
          >
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-[#a0da39]/20 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-[#1f6d62]" />
            </div>
            <h3 className="text-2xl font-extrabold text-[#222] mb-1">You're on the list!</h3>
            <p className="text-sm text-[#666] max-w-sm mx-auto">
              We'll cover your dog's match on our next TikTok Live. Keep this reference handy.
            </p>

            <div className="my-6 mx-auto max-w-xs rounded-2xl border-2 border-dashed border-[#a0da39] bg-[#f4f7ea] px-6 py-5">
              <p className="text-[10px] uppercase tracking-[3px] text-[#666] font-bold">Your Reference</p>
              <p className="mt-2 text-4xl font-extrabold tracking-widest text-[#1f6d62]" data-testid="ttl-reference">
                {reference}
              </p>
              <button
                onClick={() => {
                  navigator.clipboard?.writeText(reference);
                  toast.success('Reference copied');
                }}
                className="mt-3 inline-flex items-center gap-1.5 text-xs font-semibold text-[#0FA49C] hover:underline"
                data-testid="ttl-copy-btn"
              >
                <Copy className="w-3.5 h-3.5" /> Copy reference
              </button>
            </div>

            {nextDate && (
              <div className="mb-6 inline-block rounded-xl bg-[#1f6d62]/10 border border-[#1f6d62]/20 px-4 py-2 text-sm text-[#1f6d62] font-semibold" data-testid="ttl-next-date">
                Next TikTok Live: {nextDate}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 justify-center">
              <Button
                onClick={reset}
                variant="outline"
                className="rounded-xl px-6"
                data-testid="ttl-submit-another-btn"
              >
                Submit another bag
              </Button>
            </div>

            <p className="text-xs text-[#999] mt-6">
              We've emailed your reference to <strong>{email}</strong>. Check your inbox (and spam) if you don't see it.
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default TikTokLive;
